// ---------------------------------------------------------------------------
// V7's screens: the credential panel on a worker, and the credential catalogue.
//
// DELIBERATELY MINIMAL, BY INSTRUCTION AND BY JUDGEMENT. The console's layout
// direction is being replaced (docs/11-console-design-system.md, landing before
// V10), so these screens invest in CORRECTNESS — right data, right states, right
// semantics — and in nothing visual. Every class here already exists; this file
// introduces no colour token, no new component and no bespoke layout, so the
// rework at V10 is a values change rather than a rewrite.
//
// THREE THINGS IT DOES DO PROPERLY, BECAUSE THEY ARE NOT VISUAL:
//
// 1. HISTORY IS VISIBLE. The temporal design (09-... §V7.1) is the whole point
//    of this slice, and a screen that showed only current rows would make it
//    invisible in the product — which is how a later slice deletes it without
//    noticing. Superseded rows render, greyed and dated, under a heading that
//    says what they are.
//
// 2. EVERY VERDICT CARRIES ITS SENTENCE. `credentialVerdict` returns a tone, a
//    label and a REASON, and the reason is rendered. Absolute rule 6 arrives
//    with the rule rather than three slices after it.
//
// 3. NO VOCABULARY IS WRITTEN HERE. Verification methods, document kinds and
//    credential types are all fetched. There is no array of method names in this
//    file, which is what stops the console and V8's gate drifting apart.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { send } from "../api/auth";
import {
  console_,
  type CredentialType,
  type DocumentKindOption,
  type VerificationMethodOption as MethodOption,
  type WorkerCredential,
} from "../api/console";
import { FileDrop } from "../design/FileDrop";
import { StatusChip } from "../design/StatusChip";
import { credentialTypeChanges, credentialVerdict } from "../system/credentials";
import { localDateInput } from "../system/format";
import { useT } from "../i18n/LocaleProvider";
import { localName, translateLabel, type Locale } from "../i18n/translate";
import { AddButton } from "../design/AddLink";
import { useSession } from "../session/SessionProvider";

function useVocabularies(includeRetired = false) {
  const { apiBase } = useSession();
  const [types, setTypes] = useState<CredentialType[]>([]);
  const [methods, setMethods] = useState<MethodOption[]>([]);
  const [kinds, setKinds] = useState<DocumentKindOption[]>([]);

  const reload = useCallback(async () => {
    try {
      const ct = await send<{ credential_types?: CredentialType[] }>(
        console_.listCredentialTypes(apiBase, { includeRetired }),
      );
      if (ct.status === 200 && ct.body.credential_types) setTypes(ct.body.credential_types);
      const m = await send<{ verification_methods?: MethodOption[] }>({
        method: "GET",
        url: `${apiBase}/v1/reference/verification-methods`,
        headers: {},
        credentials: "include",
      });
      if (m.status === 200 && m.body.verification_methods) setMethods(m.body.verification_methods);
      const k = await send<{ document_kinds?: DocumentKindOption[] }>({
        method: "GET",
        url: `${apiBase}/v1/reference/document-kinds`,
        headers: {},
        credentials: "include",
      });
      if (k.status === 200 && k.body.document_kinds) setKinds(k.body.document_kinds);
    } catch {
      // Left empty on purpose. An empty vocabulary makes every <select> empty
      // and every submit disabled, which is a refusal — the safe direction.
      // Inventing defaults here would be the hard-coded taxonomy this endpoint
      // exists to prevent, arriving through the error path.
    }
  }, [apiBase, includeRetired]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { types, methods, kinds, reload };
}

// ===========================================================================
// The credential panel, rendered inside a worker's detail screen.
// ===========================================================================

export function WorkerCredentials({ workerId }: { workerId: string }) {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const { types, methods, kinds } = useVocabularies();
  const [rows, setRows] = useState<WorkerCredential[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [recording, setRecording] = useState(false);

  // `document.manage`, NOT `worker.manage`. A scheduler builds the bench and
  // must not be able to assert that somebody holds a licence — otherwise a
  // scheduler blocked by V8's gate unblocks themselves. See the note at the top
  // of api/src/routes/credentials.ts.
  const mayRecord = me?.authenticated === true && me.permissions.includes("document.manage");

  const load = useCallback(async () => {
    try {
      const { status, body } = await send<{
        credentials?: WorkerCredential[];
        error?: { message?: string };
      }>(console_.listWorkerCredentials(apiBase, workerId));
      if (status !== 200 || !body.credentials) {
        setError(refusal(body.error) ?? t("We could not load their credentials."));
        return;
      }
      setRows(body.credentials);
      setError(null);
    } catch {
      setError(t("We could not reach Verbunk."));
    }
  }, [apiBase, workerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = (rows ?? []).filter((r) => r.is_current);
  const history = (rows ?? []).filter((r) => !r.is_current);
  const day = localDateInput();

  async function record(payload: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { status, body } = await send<{
        credential?: WorkerCredential;
        renewed?: boolean;
        error?: { message?: string };
      }>(console_.recordCredential(apiBase, workerId, payload));
      if (status !== 201 || !body.credential) {
        setError(refusal(body.error) ?? t("That did not work."));
        return;
      }
      // The server says which act it was; the screen does not guess.
      setNotice(body.renewed ? t("Renewed. The previous record is kept.") : t("Recorded."));
      await load();
    } catch {
      setError(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(credentialId: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.withdrawCredential(apiBase, workerId, credentialId),
      );
      if (status !== 200) {
        setError(refusal(body.error) ?? t("That did not work."));
        return;
      }
      setNotice(t("Withdrawn. The record is kept and marked closed."));
      await load();
    } catch {
      setError(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  if (rows === null && error === null) return <p className="muted">{t("Loading credentials…")}</p>;

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>{t("Credentials")}</h3>
        {mayRecord ? <AddButton label={t("Record or renew a credential")} expanded={recording} onClick={() => setRecording(!recording)} /> : null}
      </div>

      {current.length === 0 ? (
        <p className="muted">{t("Nothing recorded for this person yet.")}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Credential")}</th>
              <th scope="col">{t("Reference")}</th>
              <th scope="col">{t("Expires")}</th>
              <th scope="col">{t("State")}</th>
              <th scope="col">{t("Why")}</th>
              {mayRecord ? <th scope="col">{t("Action")}</th> : null}
            </tr>
          </thead>
          <tbody>
            {current.map((row) => {
              const type = types.find((ct) => ct.id === row.credential_type_id);
              const verdict = credentialVerdict(row, type, methods, day, t);
              return (
                <tr key={row.id}>
                  <td>
                    {type ? localName(locale, type) : t("Unknown credential")}
                    {/* Sweep S21b: the stored evidence, and its access log. */}
                    {row.document_id ? (
                      <div>
                        <Link to={`/console/documents/${row.document_id}`}>{t("View the evidence")}</Link>
                      </div>
                    ) : null}
                  </td>
                  <td>{row.reference ?? "—"}</td>
                  <td>{row.expires_on ?? t("Does not expire")}</td>
                  <td>
                    <StatusChip tone={verdict.tone} label={verdict.label} />
                  </td>
                  <td className="muted">{verdict.reason}</td>
                  {mayRecord ? (
                    <td>
                      <button
                        type="button"
                        className="btn btn-quiet"
                        disabled={busy}
                        onClick={() => void withdraw(row.id)}
                      >
                        {t("Withdraw")}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* HISTORY IS PART OF THE PRODUCT, NOT A DEBUG VIEW. It is what a Proof
          Pack will attest to, and a person has to be able to see it. */}
      {history.length > 0 ? (
        <>
          <button
            type="button"
            className="btn btn-quiet"
            aria-expanded={showHistory}
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory
              ? t("Hide earlier records")
              : history.length === 1
                ? t("Show 1 earlier record")
                : t("Show {count} earlier records", { count: history.length })}
          </button>
          {showHistory ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Credential")}</th>
                  <th scope="col">{t("Said it expired")}</th>
                  <th scope="col">{t("In force from")}</th>
                  <th scope="col">{t("Until")}</th>
                  <th scope="col">{t("State")}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => {
                  const type = types.find((ct) => ct.id === row.credential_type_id);
                  const verdict = credentialVerdict(row, type, methods, day, t);
                  return (
                    <tr key={row.id}>
                      <td>{type ? localName(locale, type) : t("Unknown credential")}</td>
                      <td>{row.expires_on ?? t("Did not expire")}</td>
                      <td>{row.valid_from.slice(0, 10)}</td>
                      <td>{row.valid_to ? row.valid_to.slice(0, 10) : "—"}</td>
                      <td>
                        <StatusChip tone={verdict.tone} label={verdict.label} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </>
      ) : null}

      {mayRecord && recording ? (
        <RecordCredentialForm
          types={types}
          methods={methods}
          kinds={kinds}
          busy={busy}
          workerId={workerId}
          onRecord={async (payload) => {
            await record(payload);
            setRecording(false);
          }}
          onCancel={() => setRecording(false)}
        />
      ) : mayRecord ? null : (
        <p className="muted">
          {t("Recording and verifying credentials is done by a compliance user, not a scheduler.")}
        </p>
      )}

      {notice ? (
        <div className="form-message" role="status">
          <StatusChip tone="live" label={t("Saved")} />
          <span>{notice}</span>
        </div>
      ) : null}
      {error ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not saved")} />
          <span>{error}</span>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The record/renew form.
//
// EVERY CAPABILITY <select> STARTS EMPTY AND THE SUBMIT IS DISABLED UNTIL IT IS
// CHOSEN. V5's costliest defect was a form whose first option was `owner`,
// picked by an array index; the generalisation established at V6 is that
// anywhere the product chooses a value on somebody's behalf, ask what the worst
// option in the list is. Here the worst option is `manually_verified`, which
// would assert that a human checked a document nobody has looked at.
// ---------------------------------------------------------------------------

function RecordCredentialForm({
  types,
  methods,
  kinds,
  busy,
  workerId,
  onRecord,
  onCancel,
}: {
  types: CredentialType[];
  methods: MethodOption[];
  kinds: DocumentKindOption[];
  busy: boolean;
  workerId: string;
  onRecord: (payload: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}) {
  const { apiBase } = useSession();
  const { t, refusal, locale } = useT();
  const [typeId, setTypeId] = useState("");
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const chosenType = types.find((ct) => ct.id === typeId);
  const chosenMethod = methods.find((m) => m.id === method);
  const ready = typeId !== "" && method !== "" && !busy;

  // The document kind a credential's evidence belongs to is the one that
  // REQUIRES a credential type — read from the data, never named here.
  const evidenceKind = kinds.find((k) => k.requires_credential_type);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setUploadError(null);

    let documentId: string | null = null;
    if (file && evidenceKind) {
      try {
        const buffer = new Uint8Array(await file.arrayBuffer());
        let binary = "";
        for (let i = 0; i < buffer.length; i += 0x8000) {
          binary += String.fromCharCode(...buffer.subarray(i, i + 0x8000));
        }
        const { status, body } = await send<{
          document?: { id: string };
          error?: { message?: string };
        }>(
          console_.uploadDocument(apiBase, workerId, {
            kind: evidenceKind.id,
            credential_type_id: typeId,
            filename: file.name,
            content_type: file.type,
            content_base64: btoa(binary),
          }),
        );
        if (status !== 201 || !body.document) {
          setUploadError(refusal(body.error) ?? t("That file could not be stored."));
          return;
        }
        documentId = body.document.id;
      } catch {
        setUploadError(t("That file could not be read."));
        return;
      }
    }

    await onRecord({
      credential_type_id: typeId,
      verification_method: method,
      reference: reference.trim() === "" ? null : reference.trim(),
      issued_on: issuedOn === "" ? null : issuedOn,
      expires_on: expiresOn === "" ? null : expiresOn,
      document_id: documentId,
    });

    setReference("");
    setIssuedOn("");
    setExpiresOn("");
    setFile(null);
  }

  return (
    <form className="stack" onSubmit={(e) => void submit(e)}>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="cred-type">{t("Credential")}</label>
          <select id="cred-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">{t("Choose a credential…")}</option>
            {types.map((ct) => (
              <option key={ct.id} value={ct.id}>
                {localName(locale, ct)}
                {ct.is_blocking ? ` ${t("(required)")}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="cred-method">{t("How was it checked")}</label>
          <select id="cred-method" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">{t("Choose how it was checked…")}</option>
            {methods.map((m) => (
              <option key={m.id} value={m.id}>
                {translateLabel(locale, m.label)}
              </option>
            ))}
          </select>
          {chosenMethod ? <p className="field-hint">{translateLabel(locale, chosenMethod.description)}</p> : null}
        </div>
      </div>

      {/* THE CONSEQUENCE, STATED BEFORE SUBMIT RATHER THAN AFTER A 409. The
          database will refuse this combination; saying so here means a person
          understands why rather than seeing a rejection. */}
      {chosenType?.is_blocking && chosenMethod && !chosenMethod.satisfies_blocking ? (
        <div className="form-message" role="status">
          <StatusChip tone="attention" label={t("Will not count")} />
          <span>
            {t("This credential is required, so a self-declaration or an uploaded image is not enough. It will be recorded, and this person still will not be assignable.")}
          </span>
        </div>
      ) : null}

      <div className="form-grid form-grid-3">
        <div className="field">
          <label htmlFor="cred-reference">{t("Reference number")}</label>
          <input id="cred-reference" value={reference} maxLength={80} onChange={(e) => setReference(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="cred-issued">{t("Issued on")}</label>
          <input id="cred-issued" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="cred-expires">{t("Expires on")}</label>
          <input id="cred-expires" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
          <p className="field-hint">
            {chosenType?.validity_months
              ? t("Leave blank to count {months} months from the issue date.", { months: chosenType.validity_months })
              : t("Leave blank if it does not expire.")}
          </p>
        </div>
      </div>

      {evidenceKind ? (
        <div className="field">
          <label htmlFor="cred-file">{t("Evidence")}</label>
          {/* NOT A GENERAL-PURPOSE FILE FIELD. It is bound to the credential
              chosen above, and the server refuses a document of this kind that
              does not name one (09-... §V7.3). */}
          <FileDrop
            id="cred-file"
            accept="application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp,image/tiff"
            required={chosenType?.document_required === true}
            file={file}
            onFile={setFile}
          />
          <p className="field-hint">
            {chosenType?.document_required ? `${t("This credential cannot be recorded without its document.")} ` : ""}
            {translateLabel(locale, evidenceKind.description)}
          </p>
        </div>
      ) : null}

      <div className="row">
        <button type="submit" className="btn" disabled={!ready}>
          {t("Record")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>

      {uploadError ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not stored")} />
          <span>{uploadError}</span>
        </div>
      ) : null}
    </form>
  );
}

// ===========================================================================
// /console/credential-types — the catalogue.
//
// V9 replaces this with the config-pack editor. It exists at V7 because
// otherwise an organisation has no way to say what it requires, and V8's gate
// would have nothing to gate on.
// ===========================================================================

// Tour T-34. A credential made in a language other than English carries its name in the
// reader's language too, so the next reader in that language sees it. English is the name itself.
function namesPayload(locale: Locale, value: string): { names?: Record<string, string> } {
  return locale !== "en" && value.trim() !== "" ? { names: { [locale]: value.trim() } } : {};
}

function languageName(locale: Locale): string {
  return new Intl.DisplayNames([locale], { type: "language" }).of(locale) ?? locale;
}

export function CredentialTypes() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  // S40: retired rows too, or a retired type could never be found to restore.
  const { types, methods, reload } = useVocabularies(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [localised, setLocalised] = useState("");
  const [method, setMethod] = useState("");
  const [blocking, setBlocking] = useState(false);
  const [warnDays, setWarnDays] = useState("30");
  const [editing, setEditing] = useState<string | null>(null);
  const anyOwn = types.some((ct) => !ct.is_template);
  const [adding, setAdding] = useState(false);

  const mayManage = me?.authenticated === true && me.permissions.includes("config.manage");
  const chosenMethod = methods.find((m) => m.id === method);
  // The database refuses a blocking type whose method is too weak. Saying so
  // here disables the button instead of producing a constraint error.
  const combinationAllowed = !blocking || chosenMethod?.satisfies_blocking === true;
  const ready = key.trim() !== "" && name.trim() !== "" && method !== "" && combinationAllowed && !busy;

  async function create(event: React.FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.createCredentialType(apiBase, {
          key: key.trim(),
          name: name.trim(),
          required_verification_method: method,
          is_blocking: blocking,
          warn_days: Number(warnDays) || 30,
          ...namesPayload(locale, localised),
        }),
      );
      if (status !== 201) {
        setError(refusal(body.error) ?? t("That did not work."));
        return;
      }
      setKey("");
      setName("");
      setLocalised("");
      setAdding(false);
      await reload();
    } catch {
      setError(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Credentials this organisation requires")}</h2>
        {mayManage ? <AddButton label={t("Add a credential")} expanded={adding} onClick={() => setAdding(!adding)} /> : null}
      </div>

      {mayManage && adding ? (
        <form className="stack" onSubmit={(e) => void create(e)}>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ct-name">{t("Name")}</label>
              <input id="ct-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            </div>
            {locale !== "en" ? (
              <div className="field">
                <label htmlFor="ct-local-name">{t("Name in {language}", { language: languageName(locale) })}</label>
                <input id="ct-local-name" value={localised} maxLength={120} onChange={(e) => setLocalised(e.target.value)} />
                <p className="field-hint">{t("Optional. People reading in this language see this name instead.")}</p>
              </div>
            ) : null}
            <div className="field">
              <label htmlFor="ct-key">{t("Key")}</label>
              <input id="ct-key" value={key} maxLength={60} onChange={(e) => setKey(e.target.value)} />
              <p className="field-hint">
                {t("Lower case, letters, numbers and underscores. This is what an import matches on, so it should not change once anybody is using it.")}
              </p>
            </div>
            <div className="field">
              <label htmlFor="ct-method">{t("Minimum check required")}</label>
              <select id="ct-method" value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="">{t("Choose the minimum check…")}</option>
                {methods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {translateLabel(locale, m.label)}
                  </option>
                ))}
              </select>
              {chosenMethod ? <p className="field-hint">{translateLabel(locale, chosenMethod.description)}</p> : null}
            </div>
            <div className="field">
              <label htmlFor="ct-warn">{t("Warn this many days before it expires")}</label>
              <input id="ct-warn" type="number" min={0} max={365} value={warnDays} onChange={(e) => setWarnDays(e.target.value)} />
            </div>
            <label className="check form-grid-wide">
              <input type="checkbox" checked={blocking} onChange={(e) => setBlocking(e.target.checked)} />
              {t("Nobody without this can be given work")}
            </label>
          </div>

          {blocking && chosenMethod && !chosenMethod.satisfies_blocking ? (
            <div className="form-message" role="alert">
              <StatusChip tone="failed" label={t("Not allowed")} />
              <span>
                {t("A credential nobody can be assigned without has to be checked by a person or against the issuing register. An uploaded image or a self-declaration is not enough.")}
              </span>
            </div>
          ) : null}

          <div className="row">
            <button type="submit" className="btn" disabled={!ready}>
              {t("Add")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setAdding(false)}>
              {t("Cancel")}
            </button>
          </div>
        </form>
      ) : null}

      {types.length === 0 ? (
        <p className="muted">
          {t("Nothing yet. Add the tickets, licences and certificates your work needs.")}
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Name")}</th>
              <th scope="col">{t("Required")}</th>
              <th scope="col">{t("Minimum check")}</th>
              <th scope="col">{t("Warn")}</th>
              {anyOwn ? <th scope="col">{t("Source")}</th> : null}
              {mayManage && anyOwn ? <th scope="col">{t("Action")}</th> : null}
            </tr>
          </thead>
          <tbody>
            {types.map((ct) => (
              <tr key={ct.id}>
                <td>
                  {localName(locale, ct)}
                  {ct.is_retired ? (
                    <>
                      {" "}
                      <StatusChip tone="neutral" label={t("Retired")} />
                    </>
                  ) : null}
                </td>
                <td>
                  <StatusChip
                    tone={ct.is_blocking ? "attention" : "neutral"}
                    label={ct.is_blocking ? (ct.is_statutory ? t("Required by law") : t("Required")) : t("Advisory")}
                  />
                </td>
                <td>{translateLabel(locale, methods.find((m) => m.id === ct.required_verification_method)?.label ?? ct.required_verification_method)}</td>
                <td>{t("{days} days", { days: ct.warn_days })}</td>
                {anyOwn ? <td>{ct.is_template ? t("Platform") : t("Yours")}</td> : null}
                {mayManage && anyOwn ? (
                  <td>
                    {/* A platform template is not the organisation's to change; the API says so too. */}
                    {ct.is_template ? null : (
                      <button type="button" className="btn btn-quiet" onClick={() => setEditing(ct.id)}>
                        {t("Edit")}
                      </button>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayManage && editing ? (
        <EditCredentialType
          key={editing}
          type={types.find((ct) => ct.id === editing)}
          methods={methods}
          onDone={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}

      {mayManage ? null : (
        <p className="muted">{t("Only an owner or an admin can change what the organisation requires.")}</p>
      )}

      {error ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not saved")} />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="row">
        <Link className="btn btn-quiet" to="/console">
          {t("Back to the overview")}
        </Link>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SWEEP S21b. Editing one of the organisation's own credentials.
//
// `PATCH /v1/credential-types/:id` existed since V7 and nothing called it, so a typo in
// a name or a warning period set too short was permanent. The key is not offered: it is
// what an import matches on, and the create form already says it should not change.
// Only what changed is sent, so a field this form does not show is never overwritten.
// ---------------------------------------------------------------------------

function EditCredentialType({
  type,
  methods,
  onDone,
}: {
  type: CredentialType | undefined;
  methods: MethodOption[];
  onDone: () => Promise<void>;
}) {
  const { apiBase } = useSession();
  const { t, refusal, locale } = useT();
  const [name, setName] = useState(type?.name ?? "");
  const [method, setMethod] = useState(type?.required_verification_method ?? "");
  const [blocking, setBlocking] = useState(type?.is_blocking ?? false);
  const [warnDays, setWarnDays] = useState(String(type?.warn_days ?? 30));
  const [validityMonths, setValidityMonths] = useState(type?.validity_months == null ? "" : String(type.validity_months));
  const [documentRequired, setDocumentRequired] = useState(type?.document_required ?? false);
  const [retired, setRetired] = useState(type?.is_retired ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!type) return null;

  const chosenMethod = methods.find((m) => m.id === method);
  const combinationAllowed = !blocking || chosenMethod?.satisfies_blocking === true;
  const changes = credentialTypeChanges(type, {
    name,
    method,
    blocking,
    warnDays,
    validityMonths,
    documentRequired,
    retired,
  });
  const ready =
    name.trim() !== "" && warnDays !== "" && combinationAllowed && Object.keys(changes).length > 0 && !busy;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!ready || !type) return;
    setBusy(true);
    setError(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.updateCredentialType(apiBase, type.id, changes),
      );
      if (status !== 200) {
        setError(refusal(body.error) ?? t("That did not work."));
        return;
      }
      await onDone();
    } catch {
      setError(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={(e) => void save(e)}>
      <h3>{t("Edit {name}", { name: localName(locale, type) })}</h3>

      <div className="field">
        <label htmlFor="ct-edit-name">{t("Name")}</label>
        <input id="ct-edit-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="ct-edit-method">{t("Minimum check required")}</label>
        <select id="ct-edit-method" value={method} onChange={(e) => setMethod(e.target.value)}>
          {methods.map((m) => (
            <option key={m.id} value={m.id}>
              {translateLabel(locale, m.label)}
            </option>
          ))}
        </select>
        {chosenMethod ? <p className="muted">{translateLabel(locale, chosenMethod.description)}</p> : null}
      </div>

      <div className="field">
        <label htmlFor="ct-edit-blocking">
          <input
            id="ct-edit-blocking"
            type="checkbox"
            checked={blocking}
            onChange={(e) => setBlocking(e.target.checked)}
          />{" "}
          {t("Nobody without this can be given work")}
        </label>
      </div>

      {blocking && chosenMethod && !chosenMethod.satisfies_blocking ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not allowed")} />
          <span>
            {t("A credential nobody can be assigned without has to be checked by a person or against the issuing register. An uploaded image or a self-declaration is not enough.")}
          </span>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="ct-edit-warn">{t("Warn this many days before it expires")}</label>
        <input
          id="ct-edit-warn"
          type="number"
          min={0}
          max={365}
          value={warnDays}
          onChange={(e) => setWarnDays(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="ct-edit-validity">{t("Valid for this many months")}</label>
        <input
          id="ct-edit-validity"
          type="number"
          min={1}
          max={600}
          value={validityMonths}
          onChange={(e) => setValidityMonths(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="ct-edit-document">
          <input
            id="ct-edit-document"
            type="checkbox"
            checked={documentRequired}
            onChange={(e) => setDocumentRequired(e.target.checked)}
          />{" "}
          A copy of the document has to be stored with it
        </label>
      </div>

      <div className="field">
        <label htmlFor="ct-edit-retired">
          <input
            id="ct-edit-retired"
            type="checkbox"
            checked={retired}
            onChange={(e) => setRetired(e.target.checked)}
          />{" "}
          {t("Retired: no longer offered when recording a credential")}
        </label>
      </div>

      <div className="row">
        <button type="submit" className="btn" disabled={!ready}>
          {busy ? t("Saving…") : t("Save changes")}
        </button>
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void onDone()}>
          {t("Cancel")}
        </button>
      </div>

      {error ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not saved")} />
          <span>{error}</span>
        </div>
      ) : null}
    </form>
  );
}
